import { useEffect, useMemo, useRef, useState } from 'react'
import {
    useCreateInvoiceMutation,
    useGetCoinsList,
    useMakeTransferMutation,
    useProfileQuery,
} from '../../core/services/react-query/useInternalTransfer'
import Text from '../../core/utils/Text'
import CardLayout from '../layouts/CardLayout'
import { ClipLoader, ScaleLoader } from 'react-spinners'
import { SOCKET_URL } from '../../core/constants/urls'
import { FaChevronDown } from 'react-icons/fa'
import useClickOutside from '../../core/hooks/useClickOutside'
import { useRunAfterUpdate } from '../../core/hooks/useRunAfterUpdate'
import { onInputValueChangeUtil } from '../../core/utils/useInputValueChange'
import { TbInfoCircle } from 'react-icons/tb'
import { formatNumber, stringToNumber, deformatNumber } from '../../core/utils/common'
import { useMainContext } from '../../core/contexts/main'

import InternalTransferInvoice from './transfer-invoice'
import InternalTransferResult from './transfer-result'
import ModalLayout from '../layouts/ModalLayout'
import { Link } from 'react-router-dom'
import TransfersOverview from './transfers-overview'
import { useWindowSize } from '../../core/hooks/useWindowSize'
import MobileModal from '../modals/MobileModal'
import ContactsList from './contacts-list'
import HintBox from '../common/HintBox'
import { CgChevronDoubleLeft } from 'react-icons/cg'
import { BsChevronLeft, BsPeopleFill } from 'react-icons/bs'
import { BiTime, BiX } from 'react-icons/bi'


export default function InternalTransferMain() {
    const {
        main: { theme },
    } = useMainContext()
    const { width } = useWindowSize()

    const [selectedCoin, setSelectedCoin] = useState(null)
    const [showCoinsList, setShowCoinsList] = useState(false)
    const [amount, setAmount] = useState('')
    const [receiverId, setReceiverId] = useState('')
    const [note, setNote] = useState('')
    const [validation, setValidation] = useState({ errorField: '', errorText: '' })
    const [modal, setModal] = useState({ type: '', show: false, data: null })
    const [showContacts, setShowContacts] = useState(false)
    const [showHintModal, setShowHintModal] = useState(true)
    const [amountSuggestions, setAmountSuggestions] = useState([]);
    console.log(amount);

    const { data: availableCoinsList, isLoading } = useGetCoinsList()

    const {
        mutate: createInvoice,
        isLoading: isCreatingInvoice,
        data: invoiceData,
        isSuccess: invoiceCreated,
    } = useCreateInvoiceMutation()

    useEffect(() => {
        invoiceCreated && setModal({ type: 'invoice', show: true, data: invoiceData.data.data })
    }, [invoiceCreated, invoiceData])

    const {
        mutate: makeTransfer,
        isLoading: isTransfering,
        data: transferredData,
        isSuccess: transferred,
    } = useMakeTransferMutation()

    useEffect(() => {
        transferred && setModal({ type: 'result', show: true, data: transferredData.data })
    }, [transferred, transferredData])

    const { data: profile } = useProfileQuery()
    const balance = useMemo(() => {
        return profile?.coins.find((x) => x.coin === selectedCoin?.currency)?.amount || 0
    }, [profile, selectedCoin])

    const coinsListRef = useRef()
    useClickOutside(coinsListRef, () => setShowCoinsList(false))

    const runAfterUpdate = useRunAfterUpdate()
    const onInputChange = (e) => {
        let v = e?.target?.value
        // v = onInputValueChangeUtil(e, runAfterUpdate)
        setAmount(v)
    }


    useEffect(() => {
        const generateAmountSuggestions = (balance) => {
            const suggestionCount = 4;
            const increment = balance / (suggestionCount + 1);
            const suggestionsAmount = [];

            for (let i = 1; i <= suggestionCount; i++) {
                const amount = Math.floor(increment * i);
                if (amount >= selectedCoin.minAmount) {
                    suggestionsAmount.push(amount);
                }
            }

            return suggestionsAmount;
        };
        if (balance && selectedCoin) {
            setAmountSuggestions(generateAmountSuggestions(balance));
        }
    }, [balance, selectedCoin]);

    useEffect(() => {
        resetForm()
    }, [selectedCoin])

    const isTooman = selectedCoin && selectedCoin.currency === 'irt'

    const handleSubmit = () => {
        // console.log({ note, amount, receiverId, selectedCoin })

        if (!selectedCoin) setValidation({ errorField: 'coin', errorText: 'select-an-option' })
        else if (!amount) setValidation({ errorField: 'amount', errorText: 'amount-is-required' })
        else if (
            deformatNumber(amount) > selectedCoin.maxAmount ||
            deformatNumber(amount) < selectedCoin.minAmount
        )
            setValidation({ errorField: 'amount', errorText: 'amount-range' })
        else if (
            (isTooman && deformatNumber(amount) > profile.balance) ||
            (!isTooman && deformatNumber(amount) > balance)
        )
            setValidation({ errorField: 'amount', errorText: 'amount-bigger-balance' })
        else if (!receiverId)
            setValidation({ errorField: 'receiverId', errorText: 'receiverId-is-required' })
        else if (!note.trim())
            setValidation({ errorField: 'note', errorText: 'transaction-description-is-required' })
        else {
            setValidation({ errorField: '', errorText: '' })
            const payloadAmount = parseFloat(stringToNumber(amount))
            createInvoice({
                toId: receiverId,
                currency: selectedCoin.currency,
                amount: payloadAmount,
                note,
            })
        }
    }

    const resetForm = () => {
        setValidation({ errorField: '', errorText: '' })
        setAmount('')
        setReceiverId('')
        setNote('')
    }

    return (
        <>
            <CardLayout className='pt-10'>
                {/* Transfer Hint */}
                {
                    width < 1024 ? (
                        <MobileModal isOpen={showHintModal} onClose={() => { setShowHintModal(false) }}>
                            <div className='flex justify-center'>
                                <HintBox type='info' body={'internal-transfer-info'} />
                            </div>
                        </MobileModal>
                    ) : (
                        <div className='flex justify-center my-6'>
                            <HintBox type='info' body={'internal-transfer-info'} className='max-w-[600px]' />
                        </div>
                    )
                }
                <h2 className='text-base font-semibold text-center'>
                    <Text tid='internal-transfer' />
                </h2>
                <div className='w-full flex justify-center h-[90%]'>
                    <div className='flex w-full max-w-lg justify-center flex-col'>
                        <div className='w-full space-y-6'>
                            <div className='flex flex-col gap-y-2 relative'>
                                <Text tid='select-coin' className='font-medium text-sm' />
                                <div
                                    className={`border h-[54px] rounded-lg cursor-pointer ${validation.errorField && validation.errorField === 'coin'
                                        ? 'border-red-400'
                                        : 'border-gray-400 dark:border-card-border'
                                        }`}
                                    onClick={() => setShowCoinsList((prevState) => !prevState)}
                                >
                                    <div className='flex items-center gap-x-5 h-full px-5 py-2'>
                                        {selectedCoin ? (
                                            <>
                                                <img
                                                    src={
                                                        selectedCoin.currency === 'irt'
                                                            ? require('../../assets/images/tooman.png')
                                                            : SOCKET_URL + `assets/icon/${selectedCoin.currency}.png`
                                                    }
                                                    width={28}
                                                    height={28}
                                                    alt={selectedCoin.currency}
                                                />
                                                <p>{selectedCoin.currency.toUpperCase()}</p>
                                                {isTooman ? (
                                                    <span className='mr-auto'>
                                                        {formatNumber(profile?.balance || 0, {
                                                            type: 'irt',
                                                        })}
                                                    </span>) :
                                                    <span className='mr-auto'>{formatNumber(balance || 0, { type: selectedCoin.currency })}</span>
                                                }

                                                <FaChevronDown
                                                    className={`transform transition-transform ${showCoinsList ? 'rotate-180' : ''
                                                        }`}
                                                    size={10}
                                                />
                                            </>
                                        ) : (
                                            <Text tid='select-an-option' className='text-xs font-extralight' />
                                        )}
                                    </div>
                                </div>
                                {validation.errorField && validation.errorField === 'coin' ? (
                                    <p className='text-xs text-red-400'>
                                        <Text tid={validation.errorText} />
                                    </p>
                                ) : null}



                                {showCoinsList ? (
                                    <div
                                        className='absolute w-full top-full border bg-body bg-white dark:bg-secondaryBg border-borderPrimary dark:border-card-border shadow-md rounded-lg'
                                        ref={coinsListRef}
                                    >
                                        {!isLoading ? (
                                            availableCoinsList?.length > 0 ? (
                                                availableCoinsList.map((item, index) => {
                                                    const isTooman = item.currency === 'irt'

                                                    return (
                                                        <>
                                                            <div
                                                                key={item.currency}
                                                                className='flex items-center gap-x-4 px-5 py-2 hover:opacity-70 rounded-lg cursor-pointer'
                                                                onClick={() => {
                                                                    setShowCoinsList(false)
                                                                    setSelectedCoin(item)
                                                                }}
                                                            >
                                                                <img
                                                                    src={
                                                                        isTooman
                                                                            ? require('../../assets/images/tooman.png')
                                                                            : SOCKET_URL + `assets/icon/${item.currency}.png`
                                                                    }
                                                                    width={isTooman ? 31 : 30}
                                                                    height={isTooman ? 31 : 30}
                                                                    alt={item.currency}
                                                                />
                                                                <p>{item.currency.toUpperCase()}</p>
                                                            </div>
                                                            {
                                                                (index + 1) !== availableCoinsList.length &&
                                                                <div className='border-b border-gray-300 mx-2' />
                                                            }
                                                        </>
                                                    )
                                                })
                                            ) : (
                                                <div className={'flex items-center justify-center py-4'}>
                                                    <img alt=' ' src={require('../../assets/images/noData.png')} width={54} />
                                                </div>
                                            )
                                        ) : (
                                            <div className='flex items-center justify-center my-4'>
                                                <ScaleLoader color='#0773F1' height='18px' width='2px' />
                                            </div>
                                        )}
                                    </div>
                                ) : null}
                            </div>

                            <div className='flex flex-col gap-y-2'>
                                <label htmlFor='deposit-amount'>
                                    <Text tid='deposit-amount' className='font-medium text-sm' />
                                </label>

                                <div
                                    className={`border rounded-lg overflow-hidden ${validation.errorField && validation.errorField === 'amount'
                                        ? 'border-red-400'
                                        : 'border-gray-200 dark:border-card-border'
                                        } flex h-[54px]`}
                                >
                                    <input
                                        type='text'
                                        id='deposit-amount'
                                        className={`h-full block w-4/5 px-4 bg-transparent placeholder:text-xs placeholder:font-extralight`}
                                        placeholder='میزان انتقال خود را وارد نمایید'
                                        value={amount}
                                        autoComplete='off'
                                        onChange={onInputChange}
                                    />
                                    <div className='w-1/5 bg-gray-light dark:bg-card-border flex items-center justify-center cursor-default text-sm font-semibold'>
                                        <Text tid={selectedCoin?.currency.toUpperCase() || '--'} />
                                    </div>
                                </div>
                                {validation.errorField && validation.errorField === 'amount' ? (
                                    <p className='text-xs text-red-400'>
                                        <Text tid={validation.errorText} />
                                    </p>
                                ) : null}
                                {!!selectedCoin ? (
                                    <div className={'flex items-center gap-1 text-xs mt-1'}>
                                        <Text tid={'balance-can-transfer'} />
                                        {isTooman ? (
                                            <span>
                                                {formatNumber(profile?.balance || 0, {
                                                    type: 'irt',
                                                })}
                                            </span>
                                        ) : (
                                            <>
                                                <span>{formatNumber(balance || 0, { type: selectedCoin.currency })}</span>
                                                <span>{selectedCoin.currency?.toUpperCase()}</span>
                                            </>
                                        )}
                                    </div>
                                ) : null}

                                {
                                    (selectedCoin && (balance !== 0) && amountSuggestions.length > 0) &&
                                    <div className='flex w-full gap-3 items-center justify-start flex-wrap'>
                                        {amountSuggestions.map((amount, index) => (
                                            <button className='rounded-md px-4 py-2 text-sm dark:bg-[#E6EBFF] dark:text-[#A2A2A1] dark:border-none border border-[#E6EBFF] flex items-center justify-center' key={index} onClick={() => setAmount(amount.toString())}>
                                                {formatNumber(amount)} {selectedCoin.currency?.toUpperCase()}
                                            </button>
                                        ))}
                                    </div>

                                }

                                <div className='space-y-2 mt-3'>
                                    <div className={'flex items-center gap-x-2 text-xs'}>
                                        <TbInfoCircle className='text-cBlue' size={16} />
                                        <Text tid='min' />:{' '}
                                        <span dir={isTooman ? 'rtl' : 'ltr'}>
                                            {formatNumber(selectedCoin?.minAmount)}{' '}
                                            {!!isTooman ? <Text tid='toman' /> : selectedCoin?.currency?.toUpperCase()}
                                        </span>
                                    </div>
                                    <div className={'flex items-center gap-x-2 text-xs'}>
                                        <TbInfoCircle className='text-cBlue' size={16} />
                                        <Text tid='max' />:{' '}
                                        <span dir={isTooman ? 'rtl' : 'ltr'}>
                                            {formatNumber(selectedCoin?.maxAmount)}{' '}
                                            {!!isTooman ? <Text tid='toman' /> : selectedCoin?.currency?.toUpperCase()}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className='flex flex-col gap-y-2'>
                                <label htmlFor='receiverId'>
                                    <Text tid='receiver-id' className='font-medium text-sm' />
                                </label>

                                <div className='flex items-center flex-col sm:flex-row gap-2 h-[108px] sm:h-[54px]'>
                                    <div className='relative w-full sm:w-2/3 px-4 text-sm rounded-lg  border h-[50%] sm:h-full pl-2'>
                                        <input
                                            type='text'
                                            value={receiverId}
                                            onChange={(e) => setReceiverId(e.target.value?.trim())}
                                            id='receiverId'
                                            placeholder='یک شناسه معتبر وارد نمایید'
                                            autoComplete='off'
                                            className={`outline-none  placeholder:text-xs bg-transparent w-full placeholder:font-extralight h-full ${validation.errorField && validation.errorField === 'receiverId'
                                                ? 'border-red-400'
                                                : 'border-borderPrimary dark:border-card-border'
                                                }`}
                                        />
                                        {
                                            receiverId ?
                                                <BiX onClick={() => { setReceiverId('') }} className='absolute top-1/2 -translate-y-1/2 left-4 cursor-pointer' />
                                                : null
                                        }
                                    </div>
                                    <button
                                        className='w-full sm:w-1/3 text-base border border-dashed border-active dark:text-white text-cBlue flex items-center justify-center gap-x-1 px-2 py-1 h-[50%] sm:h-full rounded-lg'
                                        onClick={() => setShowContacts(true)}
                                    >
                                        <BsPeopleFill />
                                        <Text tid='my-contacts' />
                                    </button>
                                </div>

                                {validation.errorField && validation.errorField === 'receiverId' ? (
                                    <p className='text-xs text-red-400'>
                                        <Text tid={validation.errorText} />
                                    </p>
                                ) : null}
                            </div>

                            <div className='flex flex-col gap-y-2'>
                                <label htmlFor='note'>
                                    <Text tid='transaction-note' className='font-medium text-sm' />
                                </label>

                                <textarea
                                    value={note}
                                    rows={6}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder='درصورت نیاز بنویسید ...'
                                    id='note'
                                    className={`text-sm bg-transparent rounded-lg border p-4 placeholder:text-xs placeholder:font-extralight ${validation.errorField && validation.errorField === 'note'
                                        ? 'border-red-400'
                                        : 'border-borderPrimary dark:border-card-border'
                                        }`}
                                ></textarea>
                                {validation.errorField && validation.errorField === 'note' ? (
                                    <p className='text-xs text-red-400'>
                                        <Text tid={validation.errorText} />
                                    </p>
                                ) : null}
                            </div>

                            <button
                                className='rounded-lg h-[54px] w-full bg-active text-white flex items-center justify-center gap-x-4'
                                onClick={handleSubmit}
                            >
                                {isCreatingInvoice ? (
                                    <>
                                        <Text tid='calculating' />
                                        <ClipLoader size={15} color={'#fff'} />
                                    </>
                                ) : (
                                    <Text tid='next-step' />
                                )}
                                <BsChevronLeft />
                            </button>
                        </div >
                    </div>
                </div>

            </CardLayout >
            {/* Transfer result modal */}
            {
                (width > 1024) && modal.type !== 'invoice' ? (
                    <ModalLayout noBg open={modal.show && modal.type !== 'invoice'} onClose={() => {
                        setModal({ data: null, show: false, type: '' })
                        // resetForm()
                    }}>
                        <div className='relative'>
                            <InternalTransferResult
                                // closeResault={setModal.bind(null, { type: '', show: false, data: null })}
                                result={modal?.data}
                            />
                        </div>
                    </ModalLayout>
                ) : (
                    <MobileModal isOpen={modal.show && modal.type !== 'invoice'} onClose={() => {
                        setModal({ data: null, show: false, type: '' })
                        // resetForm()
                    }}>
                        <div className='relative'>
                            <InternalTransferResult
                                // closeResault={setModal.bind(null, { type: '', show: false, data: null })}
                                result={modal?.data}
                            />
                        </div>
                    </MobileModal>
                )
            }
            {/* Transfer invoice preview modal */}
            {
                (width > 1024) && modal.type === 'invoice' ? (
                    <ModalLayout noBg open={modal.show && modal.type === 'invoice'} onClose={() => {
                        setModal({ data: null, show: false, type: '' })
                    }}>
                        <div className='relative'>
                            <InternalTransferInvoice
                                closeInvoice={() => { setModal({ data: null, show: false, type: '' }) }}
                                invoice={modal?.data}
                                makeTransfer={makeTransfer}
                                isTransfering={isTransfering}
                            />
                        </div>
                    </ModalLayout>
                ) :
                    (
                        <MobileModal isOpen={modal.show && modal.type === 'invoice'} onClose={() => {
                            setModal({ data: null, show: false, type: '' })
                        }}>
                            <div className='relative'>
                                <InternalTransferInvoice
                                    closeInvoice={setModal.bind(null, { type: '', show: false, data: null })}
                                    invoice={modal?.data}
                                    makeTransfer={makeTransfer}
                                    isTransfering={isTransfering}
                                />
                            </div>
                        </MobileModal>
                    )
            }
            {/* Contact list modal */}
            {
                width > 1024 ? (
                    <ModalLayout noBg open={showContacts} onClose={() => setShowContacts(false)} width={'480px'}>
                        <div className='relative'>
                            <ContactsList
                                setReceiverId={(userId) => {
                                    setShowContacts(false)
                                    setReceiverId(userId)
                                }}
                            />
                        </div>
                    </ModalLayout>
                ) : (
                    <MobileModal isOpen={showContacts} onClose={() => setShowContacts(false)}>
                        <div className='relative'>
                            <ContactsList
                                setReceiverId={(userId) => {
                                    setShowContacts(false)
                                    setReceiverId(userId)
                                }}
                            />
                        </div>
                    </MobileModal>
                )
            }
        </>
    )
}
